import { getItem, removeItem, setItem } from '../../io';
import { SmartSearchSelection } from '../context/WorkspaceContext';

const STORAGE_VERSION = 1;
const STORAGE_KEY_PREFIX = 'acolyte.smart-search.history';
const MAX_HISTORY_RESULTS = 5;
const MAX_STORED_VALUE_LENGTH = 100000;
const MAX_TEXT_LENGTH = 500;

const resultTypes = new Set<SmartSearchSelection['type']>([
  'COORDINATE',
  'SITE',
  'CADASTRAL_AREA',
  'MUNICIPALITY',
  'VILLAGE',
]);

interface StoredHistory {
  version: number;
  results: unknown;
}

function storageKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX}.v${STORAGE_VERSION}.${userId}`;
}

function isValidText(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length > 0 &&
    value.length <= MAX_TEXT_LENGTH
  );
}

function isValidCoordinate(longitude: number, latitude: number): boolean {
  return (
    Number.isFinite(longitude) &&
    Number.isFinite(latitude) &&
    longitude >= -180 &&
    longitude <= 180 &&
    latitude >= -90 &&
    latitude <= 90
  );
}

function parseBounds(
  value: unknown
): [number, number, number, number] | null | undefined {
  if (value == null) {
    return null;
  }

  if (
    !Array.isArray(value) ||
    value.length !== 4 ||
    !value.every(
      (coordinate) =>
        typeof coordinate === 'number' && Number.isFinite(coordinate)
    )
  ) {
    return undefined;
  }

  const [west, south, east, north] = value;

  if (
    !isValidCoordinate(west, south) ||
    !isValidCoordinate(east, north) ||
    west > east ||
    south > north
  ) {
    return undefined;
  }

  return [west, south, east, north];
}

function parseResult(value: unknown): SmartSearchSelection | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const result = value as Partial<SmartSearchSelection>;
  const bounds = parseBounds(result.bounds);

  if (
    !isValidText(result.id) ||
    !resultTypes.has(result.type as SmartSearchSelection['type']) ||
    !isValidText(result.label) ||
    !isValidText(result.detail) ||
    typeof result.longitude !== 'number' ||
    typeof result.latitude !== 'number' ||
    !isValidCoordinate(result.longitude, result.latitude) ||
    bounds === undefined
  ) {
    return null;
  }

  return {
    id: result.id,
    type: result.type as SmartSearchSelection['type'],
    label: result.label,
    detail: result.detail,
    longitude: result.longitude,
    latitude: result.latitude,
    bounds,
    geometry: null,
    score: 0,
  };
}

function writeRecentSearchResults(
  userId: string,
  results: SmartSearchSelection[]
): void {
  try {
    setItem(
      storageKey(userId),
      JSON.stringify({ version: STORAGE_VERSION, results })
    );
  } catch {
    // Browsers may disable storage or reject writes when their quota is full.
  }
}

export function readRecentSearchResults(
  userId: string
): SmartSearchSelection[] {
  try {
    const storedValue = getItem(storageKey(userId));

    if (!storedValue || storedValue.length > MAX_STORED_VALUE_LENGTH) {
      return [];
    }

    const history = JSON.parse(storedValue) as StoredHistory;

    if (history.version !== STORAGE_VERSION || !Array.isArray(history.results)) {
      return [];
    }

    return history.results
      .map(parseResult)
      .filter((result): result is SmartSearchSelection => result != null)
      .slice(0, MAX_HISTORY_RESULTS);
  } catch {
    return [];
  }
}

export function addRecentSearchResult(
  userId: string,
  currentResults: SmartSearchSelection[],
  result: SmartSearchSelection
): SmartSearchSelection[] {
  const storedResult = parseResult(result);

  if (!storedResult) {
    return currentResults;
  }

  const nextResults = [
    storedResult,
    ...currentResults.filter((current) => current.id !== storedResult.id),
  ].slice(0, MAX_HISTORY_RESULTS);

  writeRecentSearchResults(userId, nextResults);

  return nextResults;
}

export function clearRecentSearchResults(userId: string): void {
  try {
    removeItem(storageKey(userId));
  } catch {
    // Clearing browser storage is best-effort.
  }
}
